import React, { forwardRef } from 'react';

import type { LucideIcon } from 'lucide-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?:
		| 'primary'
		| 'secondary'
		| 'success'
		| 'danger'
		| 'warning'
		| 'ghost'
		| 'outline'
		| 'text';
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	loading?: boolean;
	icon?: LucideIcon;
	iconPosition?: 'left' | 'right';
	fullWidth?: boolean;
	children?: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			variant = 'primary',
			size = 'md',
			loading = false,
			icon: Icon,
			iconPosition = 'left',
			fullWidth = false,
			className = '',
			disabled,
			children,
			...props
		},
		ref,
	) => {
		// Base styles that apply to all buttons using design tokens
		const baseStyles = `
        inline-flex items-center justify-center font-medium
        transition-colors duration-150 ease-in-out
        border-1
        disabled:opacity-50 disabled:cursor-not-allowed
        font-primary
    `
			.trim()
			.replace(/\s+/g, ' ');

		// Variant styles using clean utility classes
		const variantStyles = {
			text: `
            text-primary-600
            hover:text-primary-700
            border border-transparent
            hover:bg-transparent
            focus:none
        `
				.trim()
				.replace(/\s+/g, ' '),
			primary: `
            bg-primary text-white
            hover:bg-primary-600
            focus:ring-primary-200
            border border-transparent
        `
				.trim()
				.replace(/\s+/g, ' '),
			secondary: `
            text-neutral-900
            hover:bg-neutral-100
            focus:ring-neutral-500
            border border-neutral-300
        `
				.trim()
				.replace(/\s+/g, ' '),
			success: `
            bg-success-600 text-white
            hover:bg-success-700
            focus:ring-success-500
            border border-transparent
        `
				.trim()
				.replace(/\s+/g, ' '),
			danger: `
            bg-error-600 text-white
            hover:bg-error-700
            focus:ring-error-500
            border border-transparent
        `
				.trim()
				.replace(/\s+/g, ' '),
			warning: `
            bg-warning-500 text-white
            hover:bg-warning-600
            focus:ring-warning-500
            border border-transparent
        `
				.trim()
				.replace(/\s+/g, ' '),
			ghost: `
            text-primary-600
            border border-transparent
            hover:bg-transparent
            focus:outline-none focus:ring-0 focus:ring-offset-0
        `
				.trim()
				.replace(/\s+/g, ' '),
			outline: `
            text-primary-700
            hover:bg-neutral-50
            focus:ring-neutral-100
            border ring-neutral-300
        `
				.trim()
				.replace(/\s+/g, ' '),
		};

		// Size styles using standard Tailwind classes with design token values
		const sizeStyles = {
			xs: `px-3 py-1 text-xs rounded-md`,
			sm: `px-3 py-2 text-sm rounded-md`,
			md: `px-4 py-2 text-sm rounded-lg`,
			lg: `px-6 py-3 text-base rounded-lg`,
			xl: `px-8 py-4 text-lg rounded-xl`,
		};

		// Icon sizes based on button size
		const iconSizes = {
			xs: 'w-3 h-3',
			sm: 'w-4 h-4',
			md: 'w-4 h-4',
			lg: 'w-5 h-5',
			xl: 'w-6 h-6',
		};

		// Loading spinner component
		const LoadingSpinner = () => (
			<div
				className={`animate-spin rounded-full border-b-2 border-current mr-2 ${iconSizes[size]}`}
			/>
		);

		const isDisabled = disabled || loading;

		return (
			<button
				ref={ref}
				className={`
        ${baseStyles}
        ${variantStyles[variant]}
        ${variant !== 'text' ? sizeStyles[size] : ''}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `
					.trim()
					.replace(/\s+/g, ' ')}
				disabled={isDisabled}
				{...props}
			>
				{loading && <LoadingSpinner />}
				{!loading && Icon && iconPosition === 'left' && (
					<Icon className={`${iconSizes[size]} ${children ? 'mr-2' : ''}`} />
				)}
				{children}
				{!loading && Icon && iconPosition === 'right' && (
					<Icon className={`${iconSizes[size]} ${children ? 'ml-2' : ''}`} />
				)}
			</button>
		);
	},
);

Button.displayName = 'Button';

export default Button;
